import { Size, Point } from '../../objects/index';

/**
 * Observes an object for changes and publishes change notifications as they occur.
 * @param changeDelegate - called any time a change is made to any state object.
 * @returns {{}}
 * @constructor
 */

export function ViewportState (changeDelegate) {
  // Copy the default state to this instance.
  const state = Object.assign({}, INITIAL_STATE);
  // Allocate a change tracker.
  const changes = [];

  // Attach state properties to this instance. We use ES5 getters/setters to track and publish changes. We want
  // these properties to be enumerable when Object.keys() is called.
  Object.keys(state).forEach(key => {
    Object.defineProperty(this, key, {
      enumerable: true,
      get () {
        return state[key];
      },
      set (val) {
        if (state[key] !== val) {
          changes.push(key);
          state[key] = val;
          changeDelegate(key, val);
        }
      }
    });
  });

  // Expose public items that aren't enumerable (hidden from Object.keys etc).
  Object.defineProperties(this, {
    /**
         * If any properties have changed since the last clear event return true.
         */
    hasChanges: {
      get () {
        return changes.length > 0;
      }
    },
    // Returns a list of all changed properties. This simplifies checks such as "has zoom changed" by centralizing
    // tracking in this class.
    changes: {
      get () {
        return changes;
      }
    },
    // Resets all changes.
    clearChanges: {
      get () {
        // ok this is weird. We return a reference to a function. The reason for this is to hide the function
        // from ownProperty calls such as "Object.keys()"
        return clearChanges;
      }
    }
  });

  function clearChanges () {
    changes.splice(0);
  }
}

// Default Viewport state properties.
const INITIAL_STATE = {
  left: 0,
  top: 0, // the left,top corner of the viewport corresponds to this zoom image coords
  width: 0,
  height: 0, // the width and height of the view in base image coords (pixels) with zoom compensation (view w,h at zoom=2 is 2x view w,h at zoom=1)

  zoom: 1.0, // the zoom factor in the system that ImageServer's GETImage likes: 0.5 = 200%, 1 = 100%, 2 = 50%, 4 = 25% (counter intuitive)
  isFitZoom: false, // flag that zoom factor is derived from "Fit" selection
  prevZoom: 1.0, // previous zoom factor

  tileL: 0,
  tileT: 0, // the tile numbers of the left, top, right, and bottom tiles of the visible viewport (not accurate, BTW)
  tileR: 0,
  tileB: 0,
  csTiles: new Size(0, 0), // the number of tiles in each dimension of the total in a fully refreshed viewport
  csTilesVisible: new Size(0, 0), // the number of tiles in each dimension  of the visible viewport

  angleDeg: 0,
  angleRad: 0, // image rotation angle
  cpRotOffset: new Point(0, 0), // image rotation div offset

  focus: 0, // the z position which will not change until 3D is implemented

  isReadOnly: false // Allows plugins to determine whether they're allowed to manipulate viewport state
};



// WEBPACK FOOTER //
// ./src/uScope/js/lib/services/rendering/ViewportState.js